import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FlaskConical, ShoppingCart } from "lucide-react";
import { Button } from "../ui/button";
import { PaymentIframe } from "../payment/PaymentIframe";
import { config } from "../../Environment";
import useRetryFetch from "../../hooks/useRetryFetch";
import { toast } from "sonner";

interface TrialModeBannerProps {
  productId?: string;
  processId?: string;
  onPurchaseComplete?: () => void;
}

function TrialModeBanner({ productId, processId, onPurchaseComplete }: TrialModeBannerProps) {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const [showPaymentIframe, setShowPaymentIframe] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handlePurchase = async () => {
    if (!productId || !processId) {
      toast.error(t("missing_payment_information"));
      return;
    }

    try {
      setIsLoading(true);
      const response = await retryFetch(
        `${config.API_BASE}/api/payment/buyActivityAndClaimTrial`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            productId,
            trialProcessId: processId,
            useIframe: true,
          }),
        }
      );

      const result = await response.json();

      if (result.type === "redirect" && result.redirect) {
        setPaymentUrl(result.redirect);
        setShowPaymentIframe(true);
      } else {
        toast.error(t("payment_initialization_failed"));
      }
    } catch (error) {
      console.error("Failed to initiate payment:", error);
      toast.error(t("payment_initialization_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentIframe(false);
    toast.success(t("payment_successful_continuing"));
    // Call parent callback to refresh process data and remove banner
    if (onPurchaseComplete) {
      onPurchaseComplete();
    }
  };

  const handlePaymentError = (error: string) => {
    toast.error(error);
    setShowPaymentIframe(false);
  };

  return (
    <>
      <div className="w-full bg-amber-50 border-b border-amber-200 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <FlaskConical className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-900">
              {t("trial_mode_banner_text")}
            </p>
          </div>
          {productId && (
            <Button
              size="sm"
              variant="default"
              onClick={handlePurchase}
              className="flex-shrink-0"
              disabled={isLoading}
            >
              <ShoppingCart className="h-4 w-4 mr-1" />
              {isLoading ? t("loading") : t("trial_mode_banner_button")}
            </Button>
          )}
        </div>
      </div>

      <PaymentIframe
        open={showPaymentIframe}
        paymentUrl={paymentUrl}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        onClose={() => setShowPaymentIframe(false)}
      />
    </>
  );
}

export default TrialModeBanner;
